/**
 * Lazy curriculum-tree loader for the "Create revision area" picker.
 * One hook call per expand: pass `parentId` (or omit for roots) and get
 * that level's children.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { CurriculumTreeChildrenResponse } from '@formency/shared';

export const curriculumTreeKeys = {
  all: ['f', 'curriculum-tree'] as const,
  children: (parentId: string | undefined) =>
    [...curriculumTreeKeys.all, 'children', parentId ?? 'root'] as const,
};

/**
 * Fetch direct children of `parentId`. Pass `undefined` to fetch the roots
 * (domain level). Cached per-parent so repeated expand/collapse is free.
 */
export function useCurriculumTreeChildren(
  parentId: string | undefined,
  options?: { enabled?: boolean; staleTime?: number }
) {
  return useQuery<CurriculumTreeChildrenResponse>({
    queryKey: curriculumTreeKeys.children(parentId),
    queryFn: () =>
      api.get<CurriculumTreeChildrenResponse>(
        parentId
          ? `/me/curriculum-tree/children?parentId=${encodeURIComponent(parentId)}`
          : '/me/curriculum-tree/children'
      ),
    staleTime: options?.staleTime ?? 10 * 60_000,
    enabled: options?.enabled ?? true,
  });
}
